import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@mlm/ui';
import {
  BASELINE_THEME_TOKENS,
  LMS_SITE,
  STOREFRONT_DEFAULT,
  hslTripletToHex,
  resolveStorefront,
  type SiteContentOverlay,
  type StorefrontConfig,
  type ThemeTokens,
} from '@mlm/shared';
import {
  getTenantFeatures,
  getTenantTheme,
  patchTenantFeatures,
  patchTenantTheme,
} from '../lib/api';
import { useHasPerm, useIsSuperAdmin } from '../lib/auth-store';
import { SitePagesEditor } from '../components/storefront/site-pages-editor';
import { ColorField } from '../components/theme/color-field';

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/** Read a token that is known to be a string (colours, font stacks). */
function token(t: ThemeTokens, key: ColorKey): string | undefined {
  const v = t[key];
  return typeof v === 'string' ? v : undefined;
}

type ColorKey = Extract<keyof ThemeTokens, string>;

/**
 * The academy palette, in the order it reads on the page: the greens, then the
 * golds, then the neutrals. These are SEPARATE from the storefront brand tokens
 * on Layout & Theme — a tenant's shop and its academy are two looks on purpose,
 * and the learning surfaces never read `primary`/`highlight`.
 */
const LMS_COLORS: { key: ColorKey; label: string; hint?: string }[] = [
  { key: 'lmsBrand', label: 'Brand green', hint: 'Buttons, active pills, prices, links' },
  { key: 'lmsBrandDark', label: 'Brand green — hover' },
  { key: 'lmsBrandDeep', label: 'Deep green', hint: 'Dark bands, footer, Creator Studio rail' },
  { key: 'lmsBrandLight', label: 'Green tint', hint: 'Active pills, progress tracks' },
  { key: 'lmsGold', label: 'Gold', hint: 'Featured badges, stars, gold buttons' },
  { key: 'lmsGoldHover', label: 'Gold — hover' },
  { key: 'lmsGoldDeep', label: 'Deep gold', hint: 'Rating numbers, kickers, pending chips' },
  { key: 'lmsGoldTint', label: 'Gold wash', hint: 'Badge and chip backgrounds' },
  { key: 'lmsPaper', label: 'Page background' },
  { key: 'lmsInk', label: 'Headings and body text' },
  { key: 'lmsMuted', label: 'Secondary text' },
  { key: 'lmsHairline', label: 'Borders' },
  { key: 'lmsCream', label: 'Text on green' },
];

/**
 * Learning Pages — the words, pictures and colours of the member-facing academy.
 *
 * Content rides the same sparse `storefront.siteContent` overlay the bespoke
 * "Site Pages" editor uses, driven by the shared `LMS_SITE` manifest: every
 * field starts on the shipped copy and only what an admin edits is stored.
 *
 * NOTE for whoever adds the next surface here: this page saves the WHOLE
 * `storefront` block, exactly as Storefront → Content and Layout & Theme already
 * do. That is why `resolveStorefront` must keep passing `siteContent` through
 * verbatim — dropping it would make every unrelated save wipe these edits.
 */
export function LearningPagesPage() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('tenant.storefront.write');
  const isSuperAdmin = useIsSuperAdmin();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'look' && isSuperAdmin ? 'look' : 'content';

  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const [draft, setDraft] = useState<StorefrontConfig | null>(null);
  useEffect(() => {
    if (q.data && !draft) {
      setDraft(clone(resolveStorefront(q.data.storefront ?? STOREFRONT_DEFAULT)));
    }
  }, [q.data, draft]);

  const save = useMutation({
    meta: { successMessage: 'Learning pages saved.' },
    mutationFn: (cfg: StorefrontConfig) => patchTenantFeatures({ storefront: cfg }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-features'] });
      // The public storefront-content query backs the member pages' copy.
      qc.invalidateQueries({ queryKey: ['storefront-content'] });
    },
  });

  if (q.isLoading || !draft) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Learning Pages</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The public course pages your members see. Everything here is copy and pictures — the
          courses, topics and instructors themselves are managed elsewhere under Learning.
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setParams(v === 'content' ? {} : { tab: v }, { replace: true })}
      >
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="look">Look</TabsTrigger>}
        </TabsList>

        <TabsContent value="content" className="space-y-4 pt-4">
          <SitePagesEditor
            manifest={LMS_SITE}
            overlay={draft.siteContent}
            onChange={(sc: SiteContentOverlay | undefined) =>
              setDraft({ ...draft, siteContent: sc })
            }
            readOnly={!canWrite}
          />
          {canWrite && (
            <div className="flex justify-end">
              <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          )}
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="look" className="pt-4">
            <LookTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/**
 * The academy palette + display face. Super-admin only, like Layout & Theme —
 * the theme endpoint is platform-tier, and a colour set is not something a
 * tenant's own staff should be able to break on a Friday.
 */
function LookTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tenant-theme'], queryFn: getTenantTheme });
  const [draft, setDraft] = useState<ThemeTokens | null>(null);
  useEffect(() => {
    if (q.data && !draft) setDraft(clone(q.data));
  }, [q.data, draft]);

  const save = useMutation({
    meta: { successMessage: 'Learning look saved.' },
    mutationFn: (patch: Partial<ThemeTokens>) => patchTenantTheme(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-theme'] });
      qc.invalidateQueries({ queryKey: ['tenant-config'] });
    },
  });

  if (q.isLoading || !draft) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  const setToken = (key: ColorKey, v: string | undefined) => setDraft({ ...draft, [key]: v });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Palette</CardTitle>
          <p className="text-xs text-muted-foreground">
            The colours of the course pages and Creator Studio. Separate from the storefront brand
            on Layout &amp; Theme — a shop and an academy are allowed to look different. A colour
            left unset falls back to the shipped green-and-gold.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            {LMS_COLORS.map((c) => {
              const triplet = token(draft, c.key) ?? token(BASELINE_THEME_TOKENS, c.key);
              return (
                <span
                  key={c.key}
                  title={c.label}
                  className="h-8 w-8 rounded-md border"
                  style={{ background: triplet ? hslTripletToHex(triplet) : undefined }}
                />
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {LMS_COLORS.map((c) => (
              <div key={c.key}>
                <ColorField
                  label={c.label}
                  value={token(draft, c.key)}
                  baseline={token(BASELINE_THEME_TOKENS, c.key)}
                  onChange={(v) => setToken(c.key, v)}
                />
                {c.hint && (
                  <p className="mt-0.5 pl-12 text-[11px] text-muted-foreground">{c.hint}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Display font</CardTitle>
          <p className="text-xs text-muted-foreground">
            Used for headings, prices and figures on the course pages and in Creator Studio. The
            body face is unchanged. A web font needs its stylesheet listed under Layout &amp; Theme
            → Fonts before it will load.
          </p>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={2}
            value={draft.lmsFontHeading ?? ''}
            placeholder={BASELINE_THEME_TOKENS.lmsFontHeading}
            onChange={(e) =>
              setDraft({
                ...draft,
                lmsFontHeading: e.target.value.trim() === '' ? undefined : e.target.value,
              })
            }
            className="font-mono text-xs"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        {/* Sends the WHOLE theme, exactly as Layout & Theme does — the endpoint
            merge-patches, so a token cleared here keeps its stored value until
            it is given a new one. Same behaviour on both pages. */}
        <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save look'}
        </Button>
      </div>
    </div>
  );
}
